import { MappedHeader } from '@shared/types/flowstep/mapping';
import { DataField } from '@shared/providers/types';
import { RangeType, ResultDataError } from '@shared/utils/flowstep';
import { IndexedRow, WorkbookApi } from '@shared/execution/workbook-api';
import { Flowstep } from '@shared/types/flowstep';
import { ConversionError } from '@shared/excel/converters';
import { MultipleErrorsError } from '@shared/utils/utils';

export const getMappedFieldNames = (
  headers: string[],
  mappings: MappedHeader[],
  providerFields: DataField[],
): [DataField[], number[]] => {
  const fields: DataField[] = [];
  const columnIndexes: number[] = [];

  headers.forEach((header, index) => {
    const mappedHeader = mappings.find((mapping: any) => mapping.excelColumnName === header);
    if (!mappedHeader) {
      throw Error(`Mapping for header ${header} not found`);
    }
    if (mappedHeader.isUsed && mappedHeader.providerField) {
      const providerField = providerFields.find((field) => field.name === mappedHeader.providerField?.name);
      if (!providerField) {
        throw Error(`Provider field ${mappedHeader.providerField?.name} not found`);
      }
      fields.push(providerField);
      columnIndexes.push(index);
    }
  });

  return [fields, columnIndexes];
};

const rowRangeExp = /(\d+)-(\d+)/;

export const stringToRowIndexes = (valuesString: string) => {
  const result: [number, number][] = [];
  const values = valuesString.split(',').map((value) => value.trim());
  for (const value of values) {
    const match = value.match(rowRangeExp);
    if (match) {
      const start = Number.parseInt(match[1]);
      const end = Number.parseInt(match[2]);
      if (!isNaN(start) && !isNaN(end)) {
        result.push([start - 1, end - 1]);
      }
    } else {
      const number = Number.parseInt(value);
      if (!isNaN(number)) {
        result.push([number - 1, number - 1]);
      }
    }
  }
  return result;
};

export const getRowIndexesToRead = async (flowstep: Flowstep, range: any, workbookApi: WorkbookApi<any>) => {
  const rangeToApply: RangeToApply = flowstep.options.rangeToApply;

  if (rangeToApply.type === RangeType.Rows) {
    return stringToRowIndexes(rangeToApply.spansValue);
  }

  if ([RangeType.New, RangeType.Existing].includes(rangeToApply.type)) {
    const conditionFunc =
      rangeToApply.type === RangeType.New ? (values: IndexedRow) => !values[1] : (values: IndexedRow) => !!values[1];

    const columnIndex = rangeToApply.nonEmptyColumns[0];
    const columnRange = workbookApi.getRangeColumn(range, columnIndex);
    const columnValueStream = await workbookApi.readRangeData(columnRange);

    const rowIndexes: [number, number][] = [];
    await columnValueStream
      .doto((values: IndexedRow) => {
        const matchesCondition = conditionFunc(values);
        if (matchesCondition) {
          const [rowIndex] = values;
          const lastAddedIndexes = rowIndexes[rowIndexes.length - 1];
          if (lastAddedIndexes && rowIndex - lastAddedIndexes[1] === 1) {
            lastAddedIndexes[1]++;
          } else {
            rowIndexes.push([rowIndex, rowIndex]);
          }
        }
      })
      .reduce(null, () => null)
      .toPromise(Promise);

    return rowIndexes;
  }

  return undefined;
};

export type RangeToApply = {
  type: string;
  spansValue: string;
  nonEmptyColumns: number[];
};

export type ReadConvertionError = {
  error: string;
  rowIndex: number;
  columnIndex: number;
};

export type ConstantConvertionError = {
  error: string;
  fieldName: string;
  value: any;
};

export class PushError extends Error {
  constructor(
    public resultError?: ResultDataError,
    public convertionError?: ConversionError,
  ) {
    super();
  }
}

export class ConstantsConversionsError extends MultipleErrorsError {
  constructor(convertionErrors: ConstantConvertionError[]) {
    super(
      convertionErrors.map((error) => `Constant ${error.fieldName} with value ${error.value} error: ${error.error}`),
      'Errors occured during constant values conversion',
    );
  }
}
